import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, Send, Trash2, Plus, Loader2, Bot, User } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Chat() {
  const { user, getPlanId, getPlanName } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [messageLimit, setMessageLimit] = useState(-1); // -1 = unlimited
  const [remainingMessages, setRemainingMessages] = useState(-1);
  const [conversationLimits, setConversationLimits] = useState({
    conversations_used: 0,
    conversations_limit: -1,
    conversations_remaining: -1,
    messages_per_conversation: 10
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user, loadConversations]);

  // Early return AFTER all hooks are called
  if (!user) {
    return null;
  }

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoadingConversations(false);
        return;
      }
      
      const response = await fetch(`${API}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Backend returns {conversations: [...], limits: {...}}
        const convList = data.conversations || (Array.isArray(data) ? data : []);
        setConversations(convList);
        
        // Update conversation limits
        if (data.limits) {
          setConversationLimits(data.limits);
        }
      } else {
        setConversations([]);
      }
    } catch (error) {
      // console.error("Error loading conversations:", error);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/chat/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentConversation(data);
        setMessages(data.messages || []);
      }
    } catch (error) {
      // console.error("Error loading conversation:", error);
      toast.error("Error al cargar la conversación");
    }
  };

  const createNewConversation = async () => {
    // Check if user can create more conversations
    if (conversationLimits.conversations_limit !== -1) {
      if (conversationLimits.conversations_remaining <= 0) {
        toast.error(`Has alcanzado el límite de ${conversationLimits.conversations_limit} conversaciones de tu plan. Actualiza para continuar.`);
        return;
      }
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/chat/conversations`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const newConv = data.conversation || data;
        setConversations([newConv, ...conversations]);
        setCurrentConversation(newConv);
        setMessages([]);
        setRemainingMessages(-1);
        setMessageLimit(-1);
        
        // Update limits
        loadConversations();
        
        toast.success("Nueva conversación creada");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear conversación");
      }
    } catch (error) {
      // console.error("Error creating conversation:", error);
      toast.error("Error al crear conversación");
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta conversación?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/chat/conversations/${conversationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        setConversations(conversations.filter(c => c.id !== conversationId));
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(null);
          setMessages([]);
        }
        toast.success("Conversación eliminada");
      }
    } catch (error) {
      // console.error("Error deleting conversation:", error);
      toast.error("Error al eliminar conversación");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setLoading(true);

    // Optimistic UI update
    const tempUserMsg = {
      id: Date.now().toString(),
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    
    // Store current messages for rollback
    const previousMessages = messages;
    setMessages([...messages, tempUserMsg]);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/chat/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: currentConversation?.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update message limits if provided
        if (data.message_limit !== undefined) {
          setMessageLimit(data.message_limit);
          setRemainingMessages(data.remaining_messages);
        }
        
        // Update with real messages from server - replace temp message with actual ones
        setMessages(prevMessages => [...prevMessages.slice(0, -1), data.user_message, data.assistant_message]);
        
        // Update current conversation ID if it was created
        if (!currentConversation) {
          setCurrentConversation({ id: data.conversation_id });
          loadConversations(); // Refresh list
        }
        
        // Show warning if approaching limit
        if (data.remaining_messages !== -1 && data.remaining_messages <= 5) {
          toast.warning(`Te quedan ${data.remaining_messages} mensajes en esta conversación. Actualiza tu plan para continuar.`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar mensaje");
        // Rollback to previous messages
        setMessages(previousMessages);
      }
    } catch (error) {
      // console.error("Error sending message:", error);
      toast.error("Error al enviar mensaje");
      // Rollback to previous messages
      setMessages(previousMessages);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <MessageCircle className="text-orange-500" size={40} />
            Chat-IA
          </h1>
          <p className="text-gray-600 mt-2">
            Pregunta sobre tus reuniones, tareas y decisiones
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Conversaciones */}
          <Card className="lg:col-span-1 h-[calc(100vh-12rem)]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Conversaciones</CardTitle>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={createNewConversation}
                  disabled={conversationLimits.conversations_limit !== -1 && conversationLimits.conversations_remaining <= 0}
                >
                  <Plus size={18} />
                </Button>
              </div>
              {/* Conversation limits indicator */}
              {conversationLimits.conversations_limit !== -1 && (
                <div className="mt-2 text-xs">
                  <div className={`flex items-center justify-between p-2 rounded ${
                    conversationLimits.conversations_remaining <= 2 
                      ? 'bg-orange-50 text-orange-700' 
                      : 'bg-gray-50 text-gray-600'
                  }`}>
                    <span>
                      {conversationLimits.conversations_used}/{conversationLimits.conversations_limit} conversaciones
                    </span>
                    <span className="font-semibold">
                      {conversationLimits.conversations_remaining} restantes
                    </span>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-16rem)]">
                {loadingConversations ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="animate-spin text-orange-500" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No hay conversaciones. ¡Crea una nueva!
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={`p-3 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors ${
                          currentConversation?.id === conv.id ? "bg-orange-100" : ""
                        }`}
                        onClick={() => loadConversation(conv.id)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {conv.title || "Nueva conversación"}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(conv.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conv.id);
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className="lg:col-span-3 h-[calc(100vh-12rem)] flex flex-col">
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-lg">
                {currentConversation?.title || "Selecciona o crea una conversación"}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="flex-1 flex flex-col p-0">
              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    <Bot className="text-orange-500 mb-4" size={64} />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      ¡Hola! Soy tu asistente de IA
                    </h3>
                    <p className="text-gray-600 max-w-md">
                      Pregúntame sobre tus reuniones, tareas, decisiones o cualquier cosa relacionada con tus minutas.
                    </p>
                    <div className="mt-6 text-left">
                      <p className="text-sm font-medium text-gray-700 mb-2">Ejemplos:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• ¿Qué reuniones tuve esta semana?</li>
                        <li>• ¿Cuáles son mis tareas pendientes?</li>
                        <li>• Resúmeme la reunión del proyecto X</li>
                        <li>• ¿Qué decisiones se tomaron en la última reunión?</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, index) => (
                      <div
                        key={msg.id || index}
                        className={`flex items-start gap-3 ${
                          msg.role === "user" ? "flex-row-reverse" : ""
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.role === "user"
                              ? "bg-orange-500 text-white"
                              : "bg-gray-200 text-gray-700"
                          }`}
                        >
                          {msg.role === "user" ? <User size={18} /> : <Bot size={18} />}
                        </div>
                        <div
                          className={`max-w-[75%] rounded-lg p-3 ${
                            msg.role === "user"
                              ? "bg-orange-500 text-white"
                              : "bg-gray-100 text-gray-900"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <p
                            className={`text-xs mt-1 ${
                              msg.role === "user" ? "text-orange-100" : "text-gray-500"
                            }`}
                          >
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <Bot size={18} />
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3">
                          <Loader2 className="animate-spin text-gray-500" size={20} />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="border-t p-4">
                {/* Message limit indicator */}
                {currentConversation && messageLimit !== -1 && remainingMessages !== -1 && (
                  <div className={`mb-3 p-2 rounded-lg text-sm ${
                    remainingMessages <= 5 
                      ? 'bg-orange-50 text-orange-700 border border-orange-200'
                      : 'bg-gray-50 text-gray-600'
                  }`}>
                    {remainingMessages > 0 ? (
                      <span>
                        💬 Te quedan <strong>{remainingMessages}</strong> de {messageLimit} mensajes en esta conversación
                      </span>
                    ) : (
                      <span className="text-red-600">
                        ⚠️ Has alcanzado el límite de mensajes. Actualiza tu plan para continuar.
                      </span>
                    )}
                  </div>
                )}
                
                <form onSubmit={sendMessage} className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Escribe tu pregunta..."
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button 
                    type="submit" 
                    disabled={!inputMessage.trim() || loading}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <Send size={20} />
                    )}
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
